import { useCallback, useMemo, useState } from 'react';
import { EmptyStateEmbedded } from 'ui/illustrations';
import { BUTTON_SIZES, BUTTON_VARIANTS, IconButton } from 'ui/button';
import { Ellipse, LongArrowLeft24, LongArrowRight24 } from 'ui/icons';
import { colors } from 'ui/theme';
import { Skeleton } from 'ui/skeleton';
import { EmbeddedContentDefinition } from 'modules/graphql';
import { getTemplateHasContent } from 'modules/campaigns/editor/components/utils';
import { EmbeddedMessage } from 'constants/messageMediums';
import { Message } from 'modules/templateEditor/EditEmbedded/preview/Message';
import { MediaLayout } from 'modules/placements/types';
import { useDisplayPriorityContext } from 'modules/placements/context/DisplayPriorityContext';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import {
  StyledMessage,
  StyledCarouselSelector,
  LeftArrowColumn,
  EllipsisColumn,
  EmptyPreviewContainer,
  RightArrowColumn,
  StyledPreviewSkeleton,
  StyledTooltip,
  StyledCaption,
} from './styled/PreviewCarousel.styled';

export const PreviewCarousel = () => {
  const [index, setIndex] = useState<number>(0);
  const { liveTemplate } = useEditableFieldsContext();

  const {
    campaignPlacement,
    campaignDisplayPriorityOrder,
    campaignsWithContent,
    isLoadingCampaignsWithContent,
  } = useDisplayPriorityContext();

  // hide campaigns that have the hidden field toggled
  const filterHiddenCampaigns = campaignsWithContent.filter((campaign) => !campaign.hidden);

  // order the filtered campaigns with contents by the "campaignDisplayPriorityOrder"
  const campaignsSorted = useMemo(
    () =>
      filterHiddenCampaigns.sort((a, b) => {
        const campaignIds = campaignDisplayPriorityOrder.map((campaign) => campaign.id);
        return campaignIds.indexOf(a.id) - campaignIds.indexOf(b.id);
      }),
    [campaignsWithContent, campaignDisplayPriorityOrder],
  );

  const currentCampaign = campaignsSorted[index];

  const numberOfCampaignsInPreview =
    campaignPlacement?.campaignLimit &&
    campaignPlacement.campaignLimit > filterHiddenCampaigns.length
      ? filterHiddenCampaigns.length
      : campaignPlacement?.campaignLimit;

  const ellipses = Array(numberOfCampaignsInPreview)
    .fill(0)
    .map((_, i) => (
      <Ellipse
        key={i}
        width={10.75}
        height={10.75}
        color={i === index ? colors.secondaryText : colors.grey25}
      />
    ));

  const PreviewTooltipContent = currentCampaign && (
    <div>
      <div>{currentCampaign.name}</div>
      <div>{`ID: ${currentCampaign.id}`}</div>
    </div>
  );

  /**
   * the call to fetchEmbeddedTemplatesAndContents does not get recalled when a campaign's template content
   * gets updated. This means we have to look to see if content is present in context and use either the content from the live template context
   * or the content from the fetchEmbeddedTemplatesAndContents call.
   */
  const getContent = useCallback(() => {
    if (currentCampaign?.templateId === liveTemplate?.templateId)
      return liveTemplate.mediumData as EmbeddedContentDefinition;
    if (getTemplateHasContent(EmbeddedMessage, currentCampaign?.content)) {
      return currentCampaign?.content;
    }
    return undefined;
  }, [liveTemplate, currentCampaign]);

  const content = getContent();

  return !isLoadingCampaignsWithContent && currentCampaign ? (
    <StyledMessage data-test="display-priority-preview-carousel">
      <StyledTooltip tooltipContent={PreviewTooltipContent}>
        {content ? (
          <Message
            content={content}
            mediaLayout={campaignPlacement?.mediaLayout || MediaLayout.TOP}
          />
        ) : (
          <EmptyPreviewContainer>
            <EmptyStateEmbedded />
            <StyledCaption>To preview, add content to the campaign.</StyledCaption>
          </EmptyPreviewContainer>
        )}
      </StyledTooltip>
      <StyledCarouselSelector>
        {index !== 0 && (
          <LeftArrowColumn>
            <IconButton
              size={BUTTON_SIZES.MEDIUM}
              variant={BUTTON_VARIANTS.GHOST}
              onClick={() => setIndex(index - 1)}
            >
              <LongArrowLeft24 />
            </IconButton>
          </LeftArrowColumn>
        )}
        {campaignPlacement?.campaignLimit && campaignPlacement?.campaignLimit > 1 && (
          <EllipsisColumn>{ellipses}</EllipsisColumn>
        )}
        {numberOfCampaignsInPreview && index !== numberOfCampaignsInPreview - 1 && (
          <RightArrowColumn>
            <IconButton
              size={BUTTON_SIZES.MEDIUM}
              variant={BUTTON_VARIANTS.GHOST}
              onClick={() => setIndex(index + 1)}
            >
              <LongArrowRight24 />
            </IconButton>
          </RightArrowColumn>
        )}
      </StyledCarouselSelector>
    </StyledMessage>
  ) : (
    <StyledPreviewSkeleton>
      <Skeleton shimmerSpeed={1250} />
    </StyledPreviewSkeleton>
  );
};
